import { FieldError, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { Form, InputGroup } from "react-bootstrap";
import { CurrencyDollar } from "react-bootstrap-icons";
import { LocalDate } from "@js-joda/core";
import { Button } from "./Controls/Button";
import { PaymentMethodBrandLabel } from "./Controls/PaymentMethodBrandLabel";
import { cents, showMoney } from "./Data/Money";
import { showDate } from "./Data/Date";
import { PlanPriceListResp } from "./Data/ApiTypes";
import { SubscriptionStatus, SubscriptionUpdateReqApi } from "./Data/ApiTransport";
import { CostProjectionDto } from "./CostProjectionSection";

function nextRenewalDate(accessEndDate: LocalDate | null): LocalDate | null {
  if (!accessEndDate) return null;
  if (accessEndDate.isBefore(LocalDate.now())) {
    return LocalDate.now().plusMonths(1).withDayOfMonth(1);
  }
  return accessEndDate;
}

/** AR invoice period start: cost-projection upcoming window, else subscription start. */
function arInvoicePeriodStart(
  nextProjection: { periodStartDate?: string } | null,
  subscriptionStart: LocalDate | null | undefined,
): LocalDate | null {
  if (nextProjection?.periodStartDate) {
    try {
      return LocalDate.parse(nextProjection.periodStartDate);
    } catch {
      /* fall through */
    }
  }
  return subscriptionStart ?? null;
}

export interface CalculatedGroupBillingPricingSectionProps {
  status: SubscriptionStatus;
  canManageMonthly: boolean;
  loading: boolean;
  canSave: boolean;
  prices: PlanPriceListResp[] | undefined;
  register: UseFormRegister<SubscriptionUpdateReqApi>;
  setValue: UseFormSetValue<SubscriptionUpdateReqApi>;
  planPriceId: number;
  planPriceError?: FieldError;
  onSave: () => void;
  isAccountsReceivable: boolean;
  isComplimentaryPlan: boolean;
  isPendingPaymentMethodVerification: boolean;
  paymentMethod: string | null;
  costProjectionLoading: boolean;
  currentProjection: CostProjectionDto | null;
  nextProjection: CostProjectionDto | null;
  furthestAccessPeriodDate: LocalDate | null;
  /** Used for AR "period starting" when projection date missing. */
  subscriptionStart?: LocalDate | null;
  onShowCostBreakdown: () => void;
  onShowBillingInfo: () => void;
}

/** Side-by-side Billing (left) + Pricing (right) for calculated groups. */
export function CalculatedGroupBillingPricingSection({
  status,
  canManageMonthly,
  loading,
  canSave,
  prices,
  register,
  setValue,
  planPriceId,
  planPriceError,
  onSave,
  isAccountsReceivable,
  isComplimentaryPlan,
  isPendingPaymentMethodVerification,
  paymentMethod,
  costProjectionLoading,
  currentProjection,
  nextProjection,
  furthestAccessPeriodDate,
  subscriptionStart = null,
  onShowCostBreakdown,
  onShowBillingInfo,
}: CalculatedGroupBillingPricingSectionProps) {
  if (status === SubscriptionStatus.NeedsSetup || status === SubscriptionStatus.Applied) {
    return null;
  }

  const priceDisabled = status !== SubscriptionStatus.Active || !canManageMonthly;
  const currentPrice = prices?.find((p) => p.planPriceId === planPriceId);
  const { onChange: onPlanPriceChange, ...planPriceRegister } = register("planPriceId", {
    required: "Plan price is required",
    valueAsNumber: true,
  });

  const upcomingTotal =
    currentProjection && nextProjection
      ? (currentProjection.total.amount ?? 0) + (nextProjection.total.amount ?? 0)
      : 0;
  const chargeDate = nextRenewalDate(furthestAccessPeriodDate) ?? furthestAccessPeriodDate;
  const chargeDateLabel = chargeDate ? showDate(chargeDate) : null;
  const arPeriodStart = arInvoicePeriodStart(nextProjection, subscriptionStart);
  const arPeriodLabel = arPeriodStart ? showDate(arPeriodStart) : null;

  return (
    <div className="row g-3 mb-3 align-items-stretch">
      {/* Billing — left */}
      <div className="col-12 col-md-6 d-flex flex-column">
        <div className="text-uppercase text-muted small fw-semibold mb-2" style={{ letterSpacing: "0.04em" }}>
          Billing
        </div>
        <div className="border rounded px-3 pt-3 pb-2 h-100 flex-grow-1 d-flex flex-column">
          {isPendingPaymentMethodVerification ? (
            <div className="d-flex align-items-center gap-2 rounded-2 px-2 py-2" style={{ backgroundColor: "#fff3cd", border: "1px solid #ffc107" }}>
              <div>
                <div className="fw-semibold" style={{ fontSize: "0.875rem", color: "#e8650a" }}>Bank account verification pending</div>
                <div style={{ fontSize: "0.78rem", color: "#a04500" }}>
                  Account will receive a link to verify their bank account
                </div>
              </div>
            </div>
          ) : isComplimentaryPlan ? (
            <div className="text-muted small">This is a complimentary plan</div>
          ) : costProjectionLoading || !currentProjection || !nextProjection ? (
            <div className="placeholder-glow">
              <span className="placeholder col-7 rounded d-inline-block" style={{ height: 22 }} />
              <div className="mt-2">
                <span className="placeholder col-5 rounded d-inline-block" style={{ height: 12 }} />
              </div>
            </div>
          ) : (
            <>
              <div className="text-muted small mb-1">
                {isAccountsReceivable ? "Next Invoice Amount" : "Next payment"}
              </div>
              <div className="fw-bold fs-5 mb-0">{showMoney(cents(upcomingTotal))}</div>
              {(isAccountsReceivable ? arPeriodLabel : chargeDateLabel) && (
                <div className="text-muted small mb-1">
                  {isAccountsReceivable
                    ? `Period starting ${arPeriodLabel}`
                    : chargeDateLabel}
                </div>
              )}
              <div className="d-flex flex-wrap align-items-center gap-1 mb-2" style={{ fontSize: "0.8rem" }}>
                {upcomingTotal > 0 && (
                  <>
                    <button
                      type="button"
                      className="btn btn-link p-0 border-0"
                      style={{
                        lineHeight: 1.2,
                        textDecoration: "none",
                        color: "var(--bs-primary)",
                        fontSize: "inherit",
                        fontWeight: 600,
                      }}
                      onClick={onShowCostBreakdown}
                    >
                      Cost breakdown
                    </button>
                    <span className="text-muted" aria-hidden="true">·</span>
                  </>
                )}
                <button
                  type="button"
                  className="btn btn-link p-0 border-0"
                  style={{
                    lineHeight: 1.2,
                    textDecoration: "none",
                    color: "var(--bs-primary)",
                    fontSize: "inherit",
                    fontWeight: 600,
                  }}
                  onClick={onShowBillingInfo}
                >
                  How billing works
                </button>
              </div>
              {paymentMethod && (
                <>
                  <hr className="my-0 mb-2 mt-auto" />
                  <PaymentMethodBrandLabel label={paymentMethod} />
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Pricing — right */}
      <div className="col-12 col-md-6 d-flex flex-column">
        <div className="text-uppercase text-muted small fw-semibold mb-2" style={{ letterSpacing: "0.04em" }}>
          Pricing
        </div>
        <div className="border rounded px-3 pt-3 pb-2 h-100 flex-grow-1">
          <div className="fw-semibold">Plan Unit Price</div>
          {currentPrice != null && (
            <div className="text-muted small mb-2">
              Current: {showMoney(cents(currentPrice.price))}
            </div>
          )}
          <div className="d-flex align-items-center gap-2">
            <InputGroup className="flex-grow-1">
              <InputGroup.Text>
                <CurrencyDollar />
              </InputGroup.Text>
              <Form.Select
                className="text-end"
                disabled={priceDisabled}
                {...planPriceRegister}
                onChange={(e) => {
                  onPlanPriceChange(e);
                  const value = parseInt(e.target.value, 10);
                  if (value !== planPriceId) {
                    setValue("planPriceId", value, { shouldDirty: true });
                  }
                }}
              >
                {prices?.map((price) => (
                  <option key={price.planPriceId} value={price.planPriceId}>
                    {showMoney(cents(price.price))}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
            <Button
              className="btn-main px-3 flex-shrink-0 rounded-pill"
              style={{ minWidth: "5.5rem", height: "calc(1.5em + 0.75rem + 2px)" }}
              type="button"
              loading={loading}
              disabled={!canSave || priceDisabled}
              onClick={onSave}
            >
              Save
            </Button>
          </div>
          {planPriceError && (
            <div className="text-danger small mt-1">{planPriceError.message}</div>
          )}
          <div className="form-text text-muted mt-2 mb-0">
            Base unit price for this plan. Updates apply starting next cycle.
          </div>
        </div>
      </div>
    </div>
  );
}
